import type { VisibilityState } from '@tanstack/react-table';
import { formatFilterOptionLabel } from '../lib/filter-labels';
import type {
  AnalyticHistoricFilter,
  ColumnMapping,
  RecordsFilter,
  TableField,
  TableFilter,
} from '../api/types';

export type FilterOption = {
  value: string;
  label: string;
  displayLabel?: string;
  selectedLabel?: string;
  name?: string;
  idValue?: string;
};

export type FilterValueState = {
  inputValue: string;
  displayValue?: string;
  selectedValues: string[];
  selectedLabels?: Record<string, string>;
  items: FilterOption[];
  isFetching: boolean;
  touched?: boolean;
  min?: string;
  max?: string;
  searchMode?: 'contains' | 'startsWith';
};

const DEFAULT_LOOKUP_LABEL_MAX_LENGTH = 52;

export const createDefaultFilterValueState = (): FilterValueState => ({
  inputValue: '',
  displayValue: '',
  selectedValues: [],
  selectedLabels: {},
  items: [],
  isFetching: false,
  touched: false,
  min: '',
  max: '',
});

export const isNumericIdentifier = (value: string): boolean => /^\d+$/.test(value.trim());

type DisplayLabelSource = {
  _id?: string;
  internalName?: string;
  optionalName?: string;
  publicName?: string;
  name?: string;
  category?: unknown;
  pilar?: unknown;
};

const getDisplayLabelAccessor = (item: DisplayLabelSource): string =>
  item.optionalName || item.internalName || item.name || item._id || '';

export const getMappingColumnName = (column: ColumnMapping): string =>
  getDisplayLabelAccessor(column);

const DOCUMENT_TOKENS = ['docum', 'document', 'ident', 'ruc', 'nit', 'dni', 'cedula'];
const SOCIAL_NAME_TOKENS = ['razonsocial', 'razon social', 'social'];
const EXPLICIT_NAME_TOKENS = ['nombre', 'name'];

const normalizeColumnText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const hasAnyToken = (value: string, tokens: string[]): boolean =>
  tokens.some(token => value.includes(token));

const isNumericLikeValue = (value: unknown): boolean => {
  if (typeof value === 'number') return true;
  if (typeof value !== 'string') return false;
  return isNumericIdentifier(value);
};

const getDisplayLabel = <T extends DisplayLabelSource>(
  item: T,
  items?: T[],
  sampleRow?: Record<string, unknown>
): string => {
  const accessor = getDisplayLabelAccessor(item);
  const baseHeader = item.publicName || item.name || accessor;
  if (!baseHeader) return accessor;

  const normalizedBaseHeader = normalizeColumnText(baseHeader);
  if (
    normalizedBaseHeader.includes('(documento)') ||
    normalizedBaseHeader.includes('(razon social)')
  ) {
    return baseHeader;
  }

  const columnsInGroup = (items ?? []).filter(candidate => {
    const candidateHeader =
      candidate.publicName || candidate.name || getDisplayLabelAccessor(candidate);
    return normalizeColumnText(candidateHeader) === normalizedBaseHeader;
  });

  const hasSiblingColumns = columnsInGroup.length > 1;
  const searchText = normalizeColumnText(
    [
      accessor,
      item.publicName,
      item.name,
      item.internalName,
      item.optionalName,
      typeof item.category === 'string' ? item.category : '',
      typeof item.pilar === 'string' ? item.pilar : '',
    ]
      .filter(Boolean)
      .join(' ')
  );

  const hasSocialToken = hasAnyToken(searchText, SOCIAL_NAME_TOKENS);
  const hasNameToken = hasAnyToken(searchText, EXPLICIT_NAME_TOKENS);
  const hasDocumentToken = hasAnyToken(searchText, DOCUMENT_TOKENS);
  const isSocialNameColumn = hasSocialToken || (hasNameToken && !hasDocumentToken);
  const isExplicitDocumentColumn = hasDocumentToken && !hasSocialToken;
  const groupHasSocialNameColumn = columnsInGroup.some(candidate => {
    const candidateSearchText = normalizeColumnText(
      [
        getMappingColumnName(candidate),
        candidate.publicName,
        candidate.name,
        candidate.internalName,
        candidate.optionalName,
        typeof candidate.category === 'string' ? candidate.category : '',
        typeof candidate.pilar === 'string' ? candidate.pilar : '',
      ]
        .filter(Boolean)
        .join(' ')
    );
    const candidateHasSocialToken = hasAnyToken(candidateSearchText, SOCIAL_NAME_TOKENS);
    const candidateHasNameToken = hasAnyToken(candidateSearchText, EXPLICIT_NAME_TOKENS);
    const candidateHasDocumentToken = hasAnyToken(candidateSearchText, DOCUMENT_TOKENS);
    return candidateHasSocialToken || (candidateHasNameToken && !candidateHasDocumentToken);
  });

  if (hasSiblingColumns && groupHasSocialNameColumn && isSocialNameColumn) {
    return `${baseHeader} (Razon social)`;
  }

  const sampleValue = accessor && sampleRow ? sampleRow[accessor] : undefined;
  const isDocumentColumn =
    isExplicitDocumentColumn ||
    (hasSiblingColumns &&
      groupHasSocialNameColumn &&
      !isSocialNameColumn &&
      isNumericLikeValue(sampleValue));

  if (isDocumentColumn) {
    return `${baseHeader} (Documento)`;
  }

  return baseHeader;
};

export const getDisplayColumnHeader = (
  column: ColumnMapping,
  mapping?: ColumnMapping[],
  sampleRow?: Record<string, unknown>
): string => getDisplayLabel(column, mapping, sampleRow);

export const getDisplayFieldLabel = (
  field: TableField,
  fields?: TableField[],
  sampleRow?: Record<string, unknown>
): string => getDisplayLabel(field, fields, sampleRow);

export const formatLookupSelectionLabel = (
  name: string,
  idValue: string,
  maxLength = DEFAULT_LOOKUP_LABEL_MAX_LENGTH
): string => {
  const normalizedName = name.trim();
  const normalizedId = idValue.trim();
  if (!normalizedName) return normalizedId;
  if (!normalizedId || normalizedName === normalizedId) return normalizedName;

  const safeMaxLength = Math.max(10, maxLength);
  const truncateTo = Math.max(8, Math.ceil(safeMaxLength / 2));
  const truncatedName =
    normalizedName.length > safeMaxLength
      ? `${normalizedName.slice(0, truncateTo).trimEnd()}...`
      : normalizedName;

  return `${truncatedName} · ${normalizedId}`;
};

export const getFilterOptionDisplayLabel = (option: FilterOption): string => {
  return option.displayLabel || option.label || option.value;
};

export const getFilterOptionSelectedLabel = (option: FilterOption): string => {
  return option.selectedLabel || option.displayLabel || option.label || option.value;
};

export const getSelectedFilterLabel = (
  state: FilterValueState | undefined,
  value: string
): string => {
  return state?.selectedLabels?.[value] ?? value;
};

export const isFilterStateActive = (
  filter: TableFilter & { fieldData?: TableField },
  state: FilterValueState | undefined
): boolean => {
  if (!state) return false;
  if (filter.filterType === 'date' && filter.matchType === 'range') {
    return Boolean(state.min) && Boolean(state.max);
  }
  if (filter.multiple) {
    return state.selectedValues.length > 0;
  }
  return Boolean(state.inputValue.trim());
};

/**
 * Si el filtro debe ofrecer una lista de opciones que se consulta al servidor.
 *
 * El legado tenía dos caminos (`daater-app/app/client/containers/TableDataDetail/Filters.js`):
 * con `autocomplete` consultaba en cada pulsación (línea 409), y con
 * `multiple && !autocomplete` precargaba entera la relación del campo una sola vez
 * (línea 369). La migración solo trajo el primero, así que los filtros del segundo grupo
 * —"Departamento Importador", "Pais Origen", "Aduanas": 33 en total— quedaron mudos. Peor:
 * `LookupFilterField` conservaba la condición del camino 2 y programaba la consulta, que
 * luego se descartaba, de modo que no salía ni una petición ni un error visible.
 *
 * Aquí se reponen los dos casos en una sola condición, que es el punto del arreglo: eran dos
 * condiciones distintas en dos ficheros lo que dejaba la consulta agendada y sin ejecutar.
 *
 * La relación del legado es hoy la maestra del campo, y por eso `multiple` solo basta con
 * maestra: sin ella la columna guarda texto crudo de una tabla de hechos de millones de filas,
 * y pedir sus valores es el escaneo completo que provocaba los 502.
 */
export const filterOffersOptionList = (
  filter: TableFilter & { fieldData?: TableField }
): boolean => {
  if (filter.autocomplete) return true;
  return filter.multiple && Boolean(filter.fieldData?.maestraId);
};

export const shouldShowGuidedText = (
  filter: TableFilter & { fieldData?: TableField },
  state: FilterValueState | undefined
): boolean => {
  if (!filter.guidedText) return false;
  if (!state?.touched) return false;
  return !isFilterStateActive(filter, state);
};

export const hydrateFiltersFromHistoric = (
  filtersList: Array<TableFilter & { fieldData?: TableField }>,
  historic: Record<string, unknown>,
  currentValues: Record<string, FilterValueState>
) => {
  const fields = (historic as { fields?: Record<string, unknown> }).fields;
  if (!fields) return currentValues;
  const next = { ...currentValues };

  filtersList.forEach(filter => {
    const fieldValue = fields[filter.fieldData?.internalName ?? ''];
    if (fieldValue === undefined || fieldValue === null) return;
    const baseState = next[filter.id] ?? createDefaultFilterValueState();

    if (filter.filterType === 'date' && filter.matchType === 'range') {
      if (typeof fieldValue === 'string') {
        const [min, max] = fieldValue.split('|');
        next[filter.id] = { ...baseState, min: min ?? '', max: max ?? '', touched: false };
      }
      return;
    }

    if (filter.filterType === 'date') {
      next[filter.id] = {
        ...baseState,
        inputValue:
          typeof fieldValue === 'string' || typeof fieldValue === 'number'
            ? String(fieldValue)
            : '',
        displayValue:
          typeof fieldValue === 'string' || typeof fieldValue === 'number'
            ? String(fieldValue)
            : '',
        touched: false,
      };
      return;
    }

    if (filter.filterType === 'numeric') {
      next[filter.id] = {
        ...baseState,
        inputValue:
          typeof fieldValue === 'string' || typeof fieldValue === 'number'
            ? String(fieldValue)
            : '',
        displayValue:
          typeof fieldValue === 'string' || typeof fieldValue === 'number'
            ? String(fieldValue)
            : '',
        touched: false,
      };
      return;
    }

    if (filter.multiple) {
      const selectedValues = Array.isArray(fieldValue)
        ? fieldValue.map(value =>
            typeof value === 'string' || typeof value === 'number' ? String(value) : ''
          )
        : [
            typeof fieldValue === 'string' || typeof fieldValue === 'number'
              ? String(fieldValue)
              : '',
          ];
      next[filter.id] = {
        ...baseState,
        inputValue: '',
        selectedValues,
        selectedLabels: selectedValues.reduce<Record<string, string>>((acc, value) => {
          acc[value] = value;
          return acc;
        }, {}),
        touched: false,
      };
      return;
    }

    next[filter.id] = {
      ...baseState,
      inputValue:
        typeof fieldValue === 'string' || typeof fieldValue === 'number' ? String(fieldValue) : '',
      displayValue:
        typeof fieldValue === 'string' || typeof fieldValue === 'number' ? String(fieldValue) : '',
      touched: false,
    };
  });

  return next;
};

export const hydrateFiltersFromSavedReport = (
  filtersList: Array<TableFilter & { fieldData?: TableField }>,
  savedFilters: AnalyticHistoricFilter[],
  currentValues: Record<string, FilterValueState>
) => {
  const next = { ...currentValues };
  const byFieldId = new Map(savedFilters.map(f => [f.fieldId, f]));

  filtersList.forEach(filter => {
    const saved = byFieldId.get(filter.fieldId);
    if (!saved) return;
    const baseState = next[filter.id] ?? createDefaultFilterValueState();
    const value = saved.value;

    if (filter.filterType === 'date' && filter.matchType === 'range') {
      if (Array.isArray(value) && value.length === 2) {
        next[filter.id] = {
          ...baseState,
          min: String(value[0] ?? ''),
          max: String(value[1] ?? ''),
          touched: false,
        };
      }
      return;
    }

    if (filter.filterType === 'date' || filter.filterType === 'numeric') {
      const stringValue =
        typeof value === 'string' || typeof value === 'number' ? String(value) : '';
      next[filter.id] = {
        ...baseState,
        inputValue: stringValue,
        displayValue: stringValue,
        touched: false,
      };
      return;
    }

    if (filter.multiple) {
      const selectedValues = Array.isArray(value)
        ? value.map(v => (typeof v === 'string' || typeof v === 'number' ? String(v) : ''))
        : [typeof value === 'string' || typeof value === 'number' ? String(value) : ''];
      next[filter.id] = {
        ...baseState,
        inputValue: '',
        selectedValues,
        // El histórico guarda la clave (el NIT), no el nombre. Cuando el API pudo resolverlo
        // contra la maestra viene en `labels`, y la ficha tiene que decir lo mismo que la
        // tarjeta de "Búsquedas recientes" de la que salió esta búsqueda.
        selectedLabels: selectedValues.reduce<Record<string, string>>((acc, v) => {
          acc[v] = formatFilterOptionLabel(saved.labels?.[v], v);
          return acc;
        }, {}),
        touched: false,
      };
      return;
    }

    const stringValue = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
    next[filter.id] = {
      ...baseState,
      inputValue: stringValue,
      // `inputValue` guarda la clave, que es lo que viaja a la consulta, y `displayValue` el
      // nombre: el mismo reparto que hace el desplegable al elegir una opción.
      displayValue: formatFilterOptionLabel(saved.labels?.[stringValue], stringValue),
      touched: false,
    };
  });

  return next;
};

export const buildFieldsPayload = (
  filtersList: Array<TableFilter & { fieldData?: TableField }>,
  values: Record<string, FilterValueState>
) => {
  const payload: Record<string, unknown> = {};
  filtersList.forEach(filter => {
    const state = values[filter.id];
    if (!state) return;
    if (filter.filterType === 'date' && filter.matchType === 'range') {
      if (state.min && state.max) {
        payload[filter.fieldData?.internalName ?? ''] = `${state.min}|${state.max}`;
      }
      return;
    }
    if (filter.filterType === 'date' || filter.filterType === 'numeric') {
      if (state.inputValue) {
        payload[filter.fieldData?.internalName ?? ''] = state.inputValue;
      }
      return;
    }
    if (filter.multiple) {
      if (state.selectedValues.length) {
        payload[filter.fieldData?.internalName ?? ''] = state.selectedValues;
      }
      return;
    }
    if (state.inputValue) {
      payload[filter.fieldData?.internalName ?? ''] = state.inputValue;
    }
  });
  return payload;
};

export const buildFieldInfo = (
  filtersList: Array<TableFilter & { fieldData?: TableField }>,
  fields: TableField[],
  values: Record<string, FilterValueState>
) => {
  return filtersList
    .map(filter => {
      const state = values[filter.id];
      if (!state) return null;
      const fieldData = fields.find(item => item.id === filter.fieldId);
      const fieldName =
        filter.filterType === 'date' ? 'Rango de fecha' : (fieldData?.publicName ?? filter.fieldId);

      if (filter.filterType === 'date' && filter.matchType === 'range') {
        if (!state.min || !state.max) return null;
        return {
          _id: filter.fieldId,
          fieldName,
          values: `${state.min}|${state.max}`,
        };
      }

      if (filter.multiple && state.selectedValues.length) {
        return {
          _id: filter.fieldId,
          fieldName,
          values: state.selectedValues.map(value => ({
            label: getSelectedFilterLabel(state, String(value)),
            value: String(value),
          })),
        };
      }

      if (state.inputValue) {
        return {
          _id: filter.fieldId,
          fieldName,
          values: [
            {
              label: String(state.displayValue || state.inputValue),
              value: String(state.inputValue),
            },
          ],
        };
      }

      return null;
    })
    .filter(Boolean);
};

export const validateDateRange = (
  filtersList: Array<TableFilter & { fieldData?: TableField }>,
  values: Record<string, FilterValueState>,
  maxDays = 15
) => {
  const hasAdditionalActiveFilter = filtersList.some(filter => {
    const state = values[filter.id];
    if (!state) return false;

    // Ignore date range filters for "additional filter" validation.
    if (filter.filterType === 'date' && filter.matchType === 'range') return false;

    if (filter.multiple) return state.selectedValues.length > 0;
    return Boolean(state.inputValue);
  });

  const dateFilters = filtersList.filter(
    filter => filter.filterType === 'date' && filter.matchType === 'range'
  );

  for (const filter of dateFilters) {
    const state = values[filter.id];
    if (!state?.min || !state?.max) continue;
    const start = Date.parse(state.min);
    const end = Date.parse(state.max);
    if (Number.isNaN(start) || Number.isNaN(end)) continue;
    const diffDays = Math.abs(end - start) / (1000 * 60 * 60 * 24);
    if (diffDays >= maxDays && !hasAdditionalActiveFilter) {
      return `Debes hacer más específica tu búsqueda: Selecciona un filtro adicional o escoge un rango de fecha menor a ${maxDays} dias`;
    }
  }

  return null;
};

const REPORT_FILE_KEYS = ['file', 'url', 'href', 'downloadUrl', 'downloadURL', 'path', 'location'];

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object') return null;
  return value as Record<string, unknown>;
};

const readErrorDetail = (value: unknown): string | null => {
  const record = asRecord(value);
  if (!record) return null;

  if (typeof record.detail === 'string' && record.detail.trim()) {
    return record.detail.trim();
  }

  if (typeof record.message === 'string' && record.message.trim()) {
    return record.message.trim();
  }

  const nestedError = asRecord(record.error);
  if (nestedError) {
    if (typeof nestedError.detail === 'string' && nestedError.detail.trim()) {
      return nestedError.detail.trim();
    }
    const nestedOutput = asRecord(nestedError.output);
    if (nestedOutput && typeof nestedOutput.message === 'string' && nestedOutput.message.trim()) {
      return nestedOutput.message.trim();
    }
  }

  return null;
};

export const extractApiErrorMessage = (error: unknown): string | null => {
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }

  const directDetail = readErrorDetail(error);
  if (directDetail) return directDetail;

  const record = asRecord(error);
  if (!record) return null;

  return readErrorDetail(record.data);
};

export const isReportGenerationInProgressError = (error: unknown): boolean => {
  const message = extractApiErrorMessage(error);
  if (!message) return false;

  const normalized = message
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  return (
    normalized.includes('operacion de generacion del archivo') ||
    normalized.includes('se encuentra una operacion de generacion')
  );
};

export const extractReportFilePath = (value: unknown, depth = 0): string | null => {
  if (depth > 4) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }

  const record = asRecord(value);
  if (!record) return null;

  for (const key of REPORT_FILE_KEYS) {
    if (!(key in record)) continue;
    const nested = extractReportFilePath(record[key], depth + 1);
    if (nested) return nested;
  }

  for (const key of ['data', 'result', 'output', 'report']) {
    if (!(key in record)) continue;
    const nested = extractReportFilePath(record[key], depth + 1);
    if (nested) return nested;
  }

  return null;
};

export const buildFiltersBody = (
  filtersList: Array<TableFilter & { fieldData?: TableField }>,
  activeValues: Record<string, FilterValueState>
): RecordsFilter[] => {
  const result: RecordsFilter[] = [];

  for (const filter of filtersList) {
    const state = activeValues[filter.id];
    if (!state) continue;

    const matchType = filter.matchType;
    let value: string | number | Array<string | number> | null = null;

    if (filter.filterType === 'date' && filter.matchType === 'range') {
      if (state.min && state.max) {
        value = [state.min, state.max];
      }
    } else if (filter.filterType === 'date' || filter.filterType === 'numeric') {
      if (state.inputValue) {
        value = state.inputValue;
      }
    } else if (filter.multiple) {
      if (state.selectedValues.length) {
        value = state.selectedValues;
      }
    } else if (state.inputValue) {
      value = state.inputValue;
    }

    if (value === null) continue;

    result.push({
      fieldId: filter.fieldId,
      matchType,
      value,
    });
  }

  return result;
};

/**
 * Converts ISO 3166-1 alpha-2 code to flag emoji
 */
export const isoToFlagEmoji = (isoCode: string): string => {
  if (isoCode === 'WORLD') return '🌐';

  const codePoints = isoCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));

  return String.fromCodePoint(...codePoints);
};

/**
 * Returns flag emoji for a country ISO code
 * Falls back to globe emoji when code is null
 */
export const getCountryFlag = (countryCode: string | null): string => {
  return isoToFlagEmoji(countryCode || 'WORLD');
};

/**
 * Spanish month name to number mapping (1-12)
 */
const MONTH_NAME_TO_NUMBER: Record<string, number> = {
  enero: 1,
  febrero: 2,
  marzo: 3,
  abril: 4,
  mayo: 5,
  junio: 6,
  julio: 7,
  agosto: 8,
  septiembre: 9,
  octubre: 10,
  noviembre: 11,
  diciembre: 12,
};

export type DateRange = {
  minDate: string; // Format: YYYY-MM-DD
  maxDate: string; // Format: YYYY-MM-DD
};

/**
 * Parses a table name to extract date range.
 * Expected format: "Expo Plus (ENERO 2024 - Junio 2025)"
 * Returns the first day of the start month and last day of the end month.
 */
export const parseDateRangeFromTableName = (tableName: string): DateRange | null => {
  // Match pattern like "(ENERO 2024 - Junio 2025)" or "(Enero 2024 - DICIEMBRE 2025)"
  const rangeRegex = /\(([a-záéíóú]+)\s+(\d{4})\s*-\s*([a-záéíóú]+)\s+(\d{4})\)/i;
  const match = tableName.match(rangeRegex);

  if (!match) {
    return null;
  }

  const [, startMonthName, startYearStr, endMonthName, endYearStr] = match;

  const startMonth = MONTH_NAME_TO_NUMBER[startMonthName.toLowerCase()];
  const endMonth = MONTH_NAME_TO_NUMBER[endMonthName.toLowerCase()];
  const startYear = parseInt(startYearStr, 10);
  const endYear = parseInt(endYearStr, 10);

  if (!startMonth || !endMonth || isNaN(startYear) || isNaN(endYear)) {
    return null;
  }

  // Build min date: first day of start month
  const minDate = `${startYear}-${String(startMonth).padStart(2, '0')}-01`;

  // Build max date: last day of end month
  const lastDayOfEndMonth = new Date(endYear, endMonth, 0).getDate();
  const maxDate = `${endYear}-${String(endMonth).padStart(2, '0')}-${String(lastDayOfEndMonth).padStart(2, '0')}`;

  return { minDate, maxDate };
};

/**
 * Creates empty filter values state for resetting filters
 */
export const createEmptyFilterValues = (
  filtersList: Array<TableFilter & { fieldData?: TableField }>
): Record<string, FilterValueState> => {
  const emptyValues: Record<string, FilterValueState> = {};

  filtersList.forEach(filter => {
    emptyValues[filter.id] = createDefaultFilterValueState();
  });

  return emptyValues;
};

/**
 * Applies date range from table name to date filters
 */
export const applyDateRangeToFilters = (
  filtersList: Array<TableFilter & { fieldData?: TableField }>,
  currentValues: Record<string, FilterValueState>,
  dateRange: DateRange
): Record<string, FilterValueState> => {
  const next = { ...currentValues };

  filtersList.forEach(filter => {
    if (filter.filterType === 'date' && filter.matchType === 'range') {
      next[filter.id] = {
        ...(next[filter.id] ?? createDefaultFilterValueState()),
        min: dateRange.minDate,
        max: dateRange.maxDate,
        touched: false,
      };
    }
  });

  return next;
};

export type YearQuickSelect = {
  year: number;
  label: string;
  range: DateRange;
};

/**
 * Parses a date string (YYYY-MM-DD) and returns year, month, day as numbers.
 * Avoids timezone issues with Date constructor.
 */
const parseDateString = (dateStr: string): { year: number; month: number; day: number } | null => {
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return {
    year: parseInt(match[1], 10),
    month: parseInt(match[2], 10),
    day: parseInt(match[3], 10),
  };
};

/**
 * Generates year quick select options based on the table's date range.
 * Each year button will set the appropriate date range:
 * - First year: from original start month to Dec 31
 * - Middle years: Jan 1 to Dec 31
 * - Last year: Jan 1 to original end month's last day
 * - Single year: restores the full original range
 */
export const generateYearQuickSelects = (tableDateRange: DateRange | null): YearQuickSelect[] => {
  if (!tableDateRange) return [];

  const startParsed = parseDateString(tableDateRange.minDate);
  const endParsed = parseDateString(tableDateRange.maxDate);

  if (!startParsed || !endParsed) return [];

  const startYear = startParsed.year;
  const endYear = endParsed.year;

  const years: YearQuickSelect[] = [];

  for (let year = startYear; year <= endYear; year++) {
    let minDate: string;
    let maxDate: string;

    if (year === startYear && year === endYear) {
      // Single year: use original range
      minDate = tableDateRange.minDate;
      maxDate = tableDateRange.maxDate;
    } else if (year === startYear) {
      // First year: from original start to Dec 31
      minDate = tableDateRange.minDate;
      maxDate = `${year}-12-31`;
    } else if (year === endYear) {
      // Last year: from Jan 1 to original end
      minDate = `${year}-01-01`;
      maxDate = tableDateRange.maxDate;
    } else {
      // Middle year: full year
      minDate = `${year}-01-01`;
      maxDate = `${year}-12-31`;
    }

    years.push({
      year,
      label: String(year),
      range: { minDate, maxDate },
    });
  }

  return years;
};

/**
 * Columnas que la tabla expone en cada modo. En una tabla de detalle el legado
 * mostraba las columnas `isForDetail` solo dentro de la vista de detalle y las
 * quitaba de la agregada; en una tabla normal la bandera no aplica.
 *
 * Es la contraparte de `detailAwareColumns` en el API: si las dos no coinciden,
 * la app pide columnas que la respuesta no trae y la grilla las pinta vacías.
 */
export const visibleFieldsForMode = (
  fields: TableField[],
  isDetailTable: boolean,
  detailed: boolean
): TableField[] =>
  !isDetailTable || detailed ? fields : fields.filter(field => !field.isForDetail);

/**
 * Get default visible columns from fields based on visibility property
 */
export const getDefaultVisibleColumns = (fields: TableField[]): string[] => {
  return fields
    .filter(field => field.isPublic)
    .map(field => field.publicName || field.internalName || field.id)
    .filter((name): name is string => Boolean(name));
};

/**
 * Convert column names array to VisibilityState object.
 * Returns empty object (all visible) if visibleColumns is empty.
 */
export const columnsToVisibilityState = (
  allColumns: string[],
  visibleColumns: string[]
): VisibilityState => {
  // If no visible columns specified, return empty object (all columns visible)
  if (!visibleColumns.length) return {};

  const state: VisibilityState = {};
  allColumns.forEach(col => {
    state[col] = visibleColumns.includes(col);
  });
  return state;
};

/**
 * Convert VisibilityState to column names array
 */
export const visibilityStateToColumns = (state: VisibilityState): string[] => {
  return Object.entries(state)
    .filter(([, visible]) => visible)
    .map(([col]) => col);
};

export const fieldIdsToVisibilityState = (
  allColumns: string[],
  mapping: ColumnMapping[],
  fields: TableField[],
  columnFieldIds: string[]
): VisibilityState => {
  if (!columnFieldIds.length) return {};

  const columnNamesByFieldId = new Map<string, string>();

  mapping.forEach(column => {
    if (!column._id) return;
    const columnName = getMappingColumnName(column);
    if (!columnName) return;
    columnNamesByFieldId.set(column._id, columnName);
  });

  fields.forEach(field => {
    const columnName = field.publicName || field.internalName || field.id;
    if (!columnName) return;
    if (!columnNamesByFieldId.has(field.id)) {
      columnNamesByFieldId.set(field.id, columnName);
    }
  });

  const visibleColumns = columnFieldIds
    .map(fieldId => columnNamesByFieldId.get(fieldId))
    .filter((columnName): columnName is string => Boolean(columnName));

  if (!visibleColumns.length) return {};

  const baseColumns = allColumns.length ? allColumns : Array.from(columnNamesByFieldId.values());
  return columnsToVisibilityState(baseColumns, visibleColumns);
};

/**
 * Daater team ID that has full access to detailed mode
 */

/**
 * Team restrictions type for detailed mode access
 */
export type TeamDetailRestrictions = {
  partidas?: string[];
  sectors?: string[];
};

/**
 * Check if user can see the detailed mode toggle.
 * Only the tables the CMS marks as detail tables have one, so the toggle stays
 * hidden unless the backend says `isDetailTable: true`. `table` is a required
 * argument — not optional — so that a caller can never drop it and silently
 * turn every table into a detail table; it still accepts `undefined` for the
 * moment before the table is known.
 */
export const canAccessDetailedMode = (
  account: Record<string, unknown> | null,
  _teamRestrictions: TeamDetailRestrictions | undefined,
  table: { isDetailTable?: boolean; name?: string } | undefined
): boolean => {
  if (!account) return false;

  // Backend explicitly marks tables that support detail mode
  return table?.isDetailTable === true;
};

/**
 * Check if user should see demo mode (limited results) when using detailed mode.
 * Demo mode is triggered when team has no restrictions configured.
 */
export const shouldUseDemoMode = (account: Record<string, unknown> | null): boolean => {
  const detail = account?.detail;
  // Sin el dato se asume demo: falla CERRADO. Es lo contrario de lo que hacía
  // la versión anterior, que comprobaba `account.team` contra el equipo de
  // Daater — un campo que en el sistema nuevo no existe, porque ya no hay
  // equipos. Esa comparación nunca era cierta y `teamRestrictions`, que salía
  // de `account.teamData.detail`, siempre llegaba indefinido, así que TODO
  // usuario que activara detalle caía en modo demo, god-admin incluido.
  if (typeof detail !== 'object' || detail === null) return true;

  return (detail as { hasAccess?: unknown }).hasAccess !== true;
};

/**
 * Maximum number of rows shown when demo mode is active.
 */
export const DEMO_ROW_LIMIT = 5;

/**
 * Returns only the first DEMO_ROW_LIMIT records when demoMode is active.
 */
export const limitDemoRecords = <T>(records: T[], demoMode: boolean): T[] =>
  demoMode ? records.slice(0, DEMO_ROW_LIMIT) : records;

/**
 * Separate filters into standard and detail-only groups
 */
export const separateFiltersByDetailMode = (
  filters: Array<TableFilter & { fieldData?: TableField }>
): {
  standardFilters: Array<TableFilter & { fieldData?: TableField }>;
  detailFilters: Array<TableFilter & { fieldData?: TableField }>;
} => {
  const standardFilters = filters.filter(f => !f.isForDetail);
  const detailFilters = filters.filter(f => f.isForDetail);
  return { standardFilters, detailFilters };
};

/**
 * Columns to request from the API for a search: every field whose column (or its `__value`
 * companion, which the API derives from the base column) is visible. Sorted so the query key
 * does not change when only the order of the visibility state changes.
 */
export const getSearchColumns = (
  fields: Array<Pick<TableField, 'internalName' | 'showKey'>>,
  visibility: VisibilityState
): string[] =>
  fields
    .filter(
      field =>
        Boolean(field.internalName) &&
        (visibility[field.internalName] !== false ||
          (field.showKey && visibility[`${field.internalName}__value`] !== false))
    )
    .map(field => field.internalName)
    .sort();

/** True when some visible column was not part of the columns the last search loaded. */
export const hasUnloadedColumns = (visibleColumns: string[], loadedColumns: string[]): boolean => {
  const loaded = new Set(loadedColumns);
  return visibleColumns.some(column => !loaded.has(column));
};
